import { useClearRefinements, useInstantSearch } from 'react-instantsearch';
import { type FC, Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon, ChevronsDownUp, ChevronsUpDown, X, XIcon } from 'lucide-react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { BooleanFilterItem, FilterItem, RatingFilter } from '@/components/sidebar/filter-item';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TagCategoryFacets } from '@/components/sidebar/tag-category-facets';
import { SearchWithDateFilter } from '@/components/search-with-date-filter';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { RefinementList } from '@/components/sidebar/refinement-list';
import { extractDateRangeClauses } from '@/utilities/search/filters';
import { useSearchStore } from '@/hooks/data/search/useSearchStore';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useSearch } from '@/hooks/search/useSearch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

const getFilterValue = (filters: string, attribute: string) => {
  const regex = new RegExp(`${attribute}\\s*:\\s*([^\\s)]+)`, 'i');
  const match = filters.match(regex);
  return match ? match[1] : null;
};

export const hasFilter = (filters: string, attribute: string, expectedValue?: string) => {
  const value = getFilterValue(filters, attribute);
  if (value === null) {
    return false;
  }
  if (expectedValue === undefined) {
    return true;
  }
  return value === expectedValue;
};

export const SearchOptions: FC = () => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});

  const { findNodePath } = useTreeStore();
  const { setSearchQuery, filterOnlyFacets, setFilterOnlyFacets } = useSearchStore();
  const { push, query, replace } = useRouter();
  const { indexUiState } = useInstantSearch();
  const { refine, canRefine } = useClearRefinements();
  const { searchFacets: facets } = useSearch();
  const { setSelectedAssets } = useGallerySelection();
  const { onTrashSearch } = useCurrentPage();

  const selectedNode = useTreeStore((state) => state.selectedNode);
  const trashedValue = useMemo(() => getFilterValue((query?.filters as string) ?? '', 'trashed'), [query?.filters]);
  const fromTrash = trashedValue !== null && trashedValue === 'true';
  const tSearch = useTranslations('search');
  const tCommon = useTranslations('common');

  const searchFacets = useMemo(() => {
    const newFacets = { ...facets };
    delete newFacets['custom'];
    // Tags are fetched directly from the search index under tags.public (rendered as a bare FilterItem)
    newFacets['tags'] = { label: 'Tags' };
    if (!fromTrash) {
      newFacets['trashed'] = { label: 'Trash', options: { trashed: { name: 'Include trash items', options: [] } } };
    }
    newFacets['rating'] = {
      label: 'Rating',
      options: {
        rating: {
          name: 'Rating',
          options: [
            { name: '5', value: 5 },
            { name: '4', value: 4 },
            { name: '3', value: 3 },
            { name: '2', value: 2 },
            { name: '1', value: 1 },
            { name: 'No Rating', value: 0 },
          ],
        },
      },
    };
    return newFacets;
  }, [fromTrash, facets]);

  // Auto-expand parent collapsibles if filters are in URL
  useEffect(() => {
    const { searchFilters } = query;
    if (!searchFilters) {
      return;
    }

    const filterArray = Array.isArray(searchFilters) ? searchFilters : [searchFilters];
    const facetsToOpen: Record<string, boolean> = {};

    filterArray.forEach((filter) => {
      const [attribute] = filter.split(':');
      // tags.facets.<slug> has two dots, so it needs its own case: the naive first-segment
      // split below would otherwise resolve it to 'tags', a group key that doesn't exist.
      const facetKey = attribute.startsWith('tags.facets.')
        ? 'tags.facets'
        : attribute.includes('.')
          ? attribute.split('.')[0]
          : attribute;
      facetsToOpen[facetKey] = true;
      // A tags.public value may be a Custom-category child, refined through that same
      // attribute (see TagCategoryFacets) — open both, since the URL can't say which.
      if (attribute === 'tags.public') {
        facetsToOpen['tags.facets'] = true;
      }
    });

    if (Object.keys(facetsToOpen).length > 0) {
      setOpenStates((prev) => ({
        ...prev,
        ...facetsToOpen,
      }));
    }
  }, [query.searchFilters]);

  const currentRefinementCount = useMemo(
    () => Object.values(indexUiState?.refinementList ?? {}).reduce((sum, list) => sum + list.length, 0),
    [indexUiState?.refinementList],
  );

  const anyOpen = useMemo(() => Object.keys(searchFacets).some((key) => openStates[key]), [searchFacets, openStates]);

  const toggleAll = () => {
    const newState = !anyOpen;
    const newOpenStates = Object.keys(searchFacets).reduce(
      (acc, key) => {
        acc[key] = newState;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    setOpenStates(newOpenStates);
  };

  const handleToggle = (facetKey: string) => {
    setOpenStates((prev) => ({
      ...prev,
      [facetKey]: !prev[facetKey],
    }));
  };

  return (
    <div className="@container/search flex h-full flex-col space-y-px">
      <div className="flex max-h-12 min-h-12 flex-row items-center justify-between gap-5 bg-highlight-base dark:text-text-inverted">
        <div className="flex items-center">
          <Button
            variant="main-toolbar"
            size="main-toolbar"
            className="ml-2 hover:bg-highlight-hover-color dark:focus-visible:bg-black/4 [&>svg]:stroke-text-inverted hover:[&>svg]:stroke-text-inverted dark:[&>svg]:stroke-text-inverted dark:hover:[&>svg]:stroke-text-inverted"
            data-cy="search-close-button"
            onClick={() => {
              let path = '/browse/';
              if (onTrashSearch) {
                path = '/trash/';
              } else if (selectedNode?.type) {
                const pathArray = findNodePath(selectedNode.node.id, 'slug');
                path = `${path}${pathArray?.join('/')}`;
              }
              // Exit search by dropping the URL params only. The InstantSearch routing
              // middleware is the sole writer of search URL state and its stateToRoute always
              // emits `query` (see utilities/search/stateMapping); calling refine('') here would
              // schedule a throttled write that re-adds `?query=` after this navigation, which
              // useIsSearching reads as still-active and reopens the panel. URL is the source of
              // truth, so navigating away is enough — the store reset below clears the input text.
              setSearchQuery('');
              setSelectedAssets([]);
              setFilterOnlyFacets([]);
              void push({
                pathname: path,
              });
            }}
            icon={X}
            iconClassName="icon-5 text-text-inverted"
            tooltip={tSearch('closeSearch')}
          />
          <p data-cy="search-sidebar-title" className="ml-1 text-base font-bold text-text-inverted">
            {tSearch('toolbarLabel')}
          </p>
        </div>

        <div className="flex">
          {(canRefine || filterOnlyFacets.length !== 0) && (
            <Button
              disabled={!canRefine && filterOnlyFacets.length === 0}
              data-cy="search-reset-button"
              className={cn(
                'group justify-center rounded-md px-2',
                (canRefine || filterOnlyFacets.length !== 0) && 'bg-highlight-dark!',
              )}
              size="sm"
              variant="ghost"
              onClick={() => {
                setFilterOnlyFacets([]);
                refine();
              }}
            >
              <FunnelIcon
                strokeWidth={2}
                className={cn(
                  'text-text-inverted icon-4.5',
                  (canRefine || filterOnlyFacets.length !== 0) && 'text-highlight-base',
                )}
              />

              {currentRefinementCount + filterOnlyFacets.length !== 0 && (
                <Badge className="relative ml-2 border-0 px-2 py-0.5 font-bold" variant="highlight" size="md">
                  <span className="invisible">{currentRefinementCount + filterOnlyFacets.length}</span>
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="group-hover:hidden">{currentRefinementCount + filterOnlyFacets.length}</span>
                    <span className="hidden group-hover:inline">
                      <XIcon className="icon-3.5 text-text-inverted" />
                    </span>
                  </span>
                </Badge>
              )}
            </Button>
          )}

          <Button
            variant="main-toolbar"
            size="main-toolbar"
            className="mx-2 hover:bg-highlight-hover-color dark:focus-visible:bg-black/4 [&>svg]:stroke-text-inverted hover:[&>svg]:stroke-text-inverted dark:[&>svg]:stroke-text-inverted dark:hover:[&>svg]:stroke-text-inverted"
            onClick={toggleAll}
            data-cy="search-collapse-button"
            icon={anyOpen ? ChevronsDownUp : ChevronsUpDown}
            iconClassName="icon-6 text-text-inverted"
            tooltip={anyOpen ? tSearch('collapseAll') : tSearch('expandAll')}
          />
        </div>
      </div>
      {(selectedNode?.type ?? onTrashSearch) && (
        <div className="flex max-h-10 min-h-10 items-center gap-5 bg-ui-container text-[13px] font-normal dark:text-text-base">
          <p className="ml-2 font-bold whitespace-nowrap">{tSearch('searchingIn')}</p>

          <div className="flex flex-1 items-center overflow-hidden">
            <div className="mr-1 flex-1 truncate text-right text-highlight-base" data-cy="search-selected-node-text">
              {onTrashSearch ? tCommon('trash') : (selectedNode?.node.name ?? '')}
            </div>
            <Button
              className="shrink-0 rounded-md px-2 dark:hover:bg-white/10"
              data-cy="search-clear-node-button"
              size="sm"
              variant="ghost"
              onClick={() => {
                const separator = ' && ';
                const preserved = extractDateRangeClauses(String(query.filters ?? ''));
                const newQuery: Record<string, string | Array<string>> = {
                  query: typeof query.query === 'string' ? query.query : '',
                  ...(query.searchFilters ? { searchFilters: query.searchFilters } : {}),
                  ...(preserved.length > 0 ? { filters: preserved.join(separator) } : {}),
                };
                void replace({ pathname: '/browse', query: newQuery }, undefined, { shallow: true });
              }}
            >
              <XIcon className="icon-4 text-text-base" />
            </Button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 bg-ui-base p-2">
        <SearchWithDateFilter />
      </div>
      <div className="flex h-full flex-col overflow-y-auto bg-ui-panel">
        {
          /* eslint-disable */
          (
            Object.entries(searchFacets) as Array<
              [string, { label: string; searchable?: boolean; options?: Record<string, any> }]
            >
          )
            /* eslint-enable */
            .map(([facetKey, facet]) => {
              return (
                <Collapsible
                  key={facetKey}
                  defaultOpen={true}
                  open={openStates[facetKey] ?? false}
                  onOpenChange={() => handleToggle(facetKey)}
                >
                  <CollapsibleTrigger
                    data-cy={`search-facet-${facetKey}-trigger`}
                    className="flex w-full items-center gap-1.5 bg-ui-light p-1.5 text-left dark:bg-ui-bevel"
                  >
                    <ChevronRightIcon
                      className={cn('icon-5.5 text-icon-base transition duration-300', {
                        'rotate-90': openStates[facetKey],
                      })}
                    />
                    <p className="font-normal">{facet?.label}</p>
                  </CollapsibleTrigger>

                  <div className={cn(openStates[facetKey] ? 'block' : 'hidden')}>
                    {facetKey === 'tags.facets' ? (
                      facet.options && <TagCategoryFacets options={facet.options} />
                    ) : facetKey === 'tags' ? (
                      <RefinementList attribute="tags.public" label={tCommon('tags')} searchable searchLabel="tags" />
                    ) : (
                      facet.options &&
                      Object.entries(facet.options).map(([optionKey, optionValue]) => (
                        <Fragment key={optionKey}>
                          {optionKey === 'trashed' ? (
                            <BooleanFilterItem
                              key={`sidebar-${facetKey}-${optionKey}`}
                              filter={{
                                attribute: optionKey,
                                label: optionValue.name ?? optionValue,
                                options: optionValue.options ?? [],
                              }}
                              setFilterOnlyFacets={setFilterOnlyFacets}
                              filterOnlyFacets={filterOnlyFacets}
                            />
                          ) : facetKey === 'rating' ? (
                            <RatingFilter
                              key={`sidebar-rating-${optionKey}`}
                              filter={{
                                attribute: 'rating',
                                label: facet.label,
                                options: optionValue.options ?? [],
                              }}
                            />
                          ) : (
                            <FilterItem
                              key={`sidebar-${facetKey}-${optionKey}`}
                              filter={{
                                attribute: facetKey === 'custom' ? optionKey : `${facetKey}.${optionKey}`,
                                searchable: facet.searchable,
                                searchLabel: optionValue.searchLabel,
                                label: optionValue.name ?? optionValue,
                                options: optionValue.options ?? [],
                                fieldType: optionValue.type,
                              }}
                            />
                          )}
                        </Fragment>
                      ))
                    )}
                  </div>
                </Collapsible>
              );
            })
        }
      </div>
    </div>
  );
};
